import {
  AppointmentTypes,
  AuthDto,
  AvailabilityTypes,
  CalendarEventTypes,
  CourseTypes,
  LocationTypes,
  LoyaltyTypes,
  PaymentDto,
  PromoCodeTypes,
  StoreTypes,
  UserTypes,
} from 'common';

import { LoginWithVerificationTokenOutputBoundary } from 'application/modules/auth/useCases/hooks/useCaseVerifyUser';
import { BookAppointmentValues } from 'application/modules/bookingWizard/useCases/hooks/useCaseConfirmReservation';

import baseApiWithReauth from './baseApiWithReauth';
import {
  AppointmentRoutes,
  AuthRoutes,
  CourseRoutes,
  EventRoutes,
  FoodOrderRoutes,
  LocationRoutes,
  RegionalBookingRoutes,
  ScheduleRoutes,
  SupportRoutes,
} from './mboApiRoutes';

const mboAPI = baseApiWithReauth
  .enhanceEndpoints({
    addTagTypes: [
      'User',
      'ClientAppointments',
      'SimulatorAppointments',
      'SimulatorAppointmentsDates',
      'LessonAppointments',
      'ClubFittingAppointments',
      'FirstLessonAppointments',
      'FirstLessonAppointmentsDates',
      'LessonAppointmentsDates',
      'ClubFittingAppointmentsDates',
      'AppointmentPricing',
      'FoodItemList',
      'GiftCards',
      'StaffAppointments',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getSimulatorLocations: builder.query<Array<LocationTypes.SimulatorLocation>, void>({
        query: () => LocationRoutes.getLocations(),
      }),
      getAvailableSimulatorRentalAppointments: builder.query<
        Array<AvailabilityTypes.SimulatorAppointmentAvailability>,
        {
          locationId: string;
          startDateTime?: string;
          endDateTime?: string;
          email?: string;
          partySize: string;
        }
      >({
        query: ({ locationId, startDateTime, endDateTime, email, partySize }) =>
          AppointmentRoutes.getAvailableSimulatorRentalAppointments(
            locationId,
            partySize,
            startDateTime,
            endDateTime,
            email,
          ),
        providesTags: ['SimulatorAppointments'],
      }),
      getAvailableSimulatorRentalDates: builder.query<
        Array<string>,
        { locationId: string; startDateTime?: string; endDateTime?: string }
      >({
        query: ({ locationId, startDateTime, endDateTime }) =>
          AppointmentRoutes.getAvailableSimulatorRentalDates(
            locationId,
            startDateTime,
            endDateTime,
          ),
        providesTags: ['SimulatorAppointmentsDates'],
      }),
      getAppointmentPricing: builder.mutation<
        AppointmentTypes.ServicePricing[],
        AppointmentTypes.AppointmentPricingRequest
      >({
        query: (body) => ({
          url: AppointmentRoutes.getAppointmentPricing(),
          method: 'POST',
          body,
        }),
      }),
      getClientAppointments: builder.query<AppointmentTypes.ClientAppointmentResponse[], void>({
        query: () => AppointmentRoutes.getClientAppointments(),
        providesTags: ['ClientAppointments'],
      }),
      getUpcomingAppointments: builder.query<AppointmentTypes.ClientAppointmentResponse[], void>({
        query: () => AppointmentRoutes.getUpcomingAppointments(),
      }),
      getHourlySimulatorPricing: builder.mutation<
        AppointmentTypes.HourlySimulatorPricingDto,
        AppointmentTypes.HourlySimulatorPricingRequestDto
      >({
        query: (body) => ({
          url: AppointmentRoutes.getHourlySimulatorPricing(),
          method: 'POST',
          body,
        }),
      }),
      cancelAppointment: builder.mutation<
        AppointmentTypes.CanceledAppointmentResponse,
        { locationId: string; body: AppointmentTypes.CancelAppointmentRequest }
      >({
        query: ({ locationId, body }) => ({
          url: AppointmentRoutes.cancelAppointment(locationId),
          method: 'POST',
          body,
        }),
        invalidatesTags: [
          'SimulatorAppointments',
          'SimulatorAppointmentsDates',
          'LessonAppointments',
          'FirstLessonAppointments',
          'ClientAppointments',
        ],
      }),
      getAvailableFirstTimeLessonsAppointments: builder.query<
        Array<AvailabilityTypes.FirstTimeLessonAppointmentAvailability>,
        { locationId: string; startDateTime?: string; endDateTime?: string; email?: string }
      >({
        query: ({ locationId, startDateTime, endDateTime, email }) =>
          AppointmentRoutes.getAvailableFirstLessonAppointments(
            locationId,
            startDateTime,
            endDateTime,
            email,
          ),
        providesTags: ['FirstLessonAppointments'],
      }),
      getAvailableStaffAppointments: builder.query<
        Array<AvailabilityTypes.StaffAppointmentAvailability>,
        {
          locationId: string;
          email?: string;
          checkAvailability?: boolean;
        }
      >({
        query: ({ locationId, email, checkAvailability }) =>
          AppointmentRoutes.getAvailableStaffAppointments(locationId, email, checkAvailability),
        providesTags: ['StaffAppointments'],
      }),
      getAvailableLessonAppointments: builder.query<
        Array<AvailabilityTypes.LessonAppointmentAvailability>,
        {
          locationId: string;
          startDateTime?: string;
          endDateTime?: string;
          staffIds?: number;
          email?: string;
          includeLastBooked?: boolean;
        }
      >({
        query: ({ locationId, startDateTime, endDateTime, staffIds, email, includeLastBooked }) =>
          AppointmentRoutes.getAvailableLessonAppointments(
            locationId,
            startDateTime,
            endDateTime,
            staffIds,
            email,
            includeLastBooked,
          ),
        providesTags: ['LessonAppointments'],
      }),
      getAvailableClubFittingAppointments: builder.query<
        Array<AvailabilityTypes.ClubFittingAppointmentAvailability>,
        {
          locationId: string;
          startDateTime: string;
          endDateTime: string;
          clubFittingId: number;
          email?: string;
        }
      >({
        query: ({ locationId, startDateTime, endDateTime, clubFittingId, email }) =>
          AppointmentRoutes.getAvailableClubFittingAppointments(
            locationId,
            startDateTime,
            endDateTime,
            clubFittingId,
            email,
          ),
        providesTags: ['ClubFittingAppointments'],
      }),
      getClubFittingTypes: builder.query<AvailabilityTypes.ClubFittingType[], string>({
        query: (locationId) => AppointmentRoutes.getClubFittingTypes(locationId),
      }),
      verifyPromoCode: builder.query<
        PromoCodeTypes.PromoCodeVerificationResponse,
        { siteId: string; promoCode: string; date: string; sessionTypeIds?: number[] }
      >({
        query: ({ siteId, promoCode, date, sessionTypeIds }) =>
          AppointmentRoutes.verifyPromoCode(siteId, promoCode, date, sessionTypeIds),
      }),
      getDailyPromoCode: builder.query<
        PromoCodeTypes.DailyPromoCodeResponse,
        { siteId: string; date: string }
      >({
        query: ({ siteId, date }) => AppointmentRoutes.getDailyPromoCode(siteId, date),
      }),
      getStaffAvailability: builder.query<
        Array<AvailabilityTypes.LessonAppointmentAvailability>,
        {
          locationId: string;
          staffId: number;
          startDateTime: string;
          endDateTime: string;
          email?: string;
        }
      >({
        query: ({ locationId, staffId, startDateTime, endDateTime, email }) =>
          AppointmentRoutes.getStaffAvailability(
            locationId,
            staffId,
            startDateTime,
            endDateTime,
            email,
          ),
      }),
      getAvailableLessonDates: builder.query<
        Array<string>,
        {
          locationId: string;
          startDateTime?: string;
          endDateTime?: string;
          staffId?: number;
          sessionTypeId?: number;
        }
      >({
        query: ({ locationId, startDateTime, endDateTime, staffId, sessionTypeId }) =>
          AppointmentRoutes.getAvailableLessonDates(
            locationId,
            startDateTime,
            endDateTime,
            staffId,
            sessionTypeId,
          ),
        providesTags: ['LessonAppointmentsDates'],
      }),
      getAvailableFirstLessonDates: builder.query<
        Array<string>,
        {
          locationId: string;
          startDateTime?: string;
          endDateTime?: string;
        }
      >({
        query: ({ locationId, startDateTime, endDateTime }) =>
          AppointmentRoutes.getAvailableFirstLessonDates(locationId, startDateTime, endDateTime),
        providesTags: ['FirstLessonAppointmentsDates'],
      }),
      getAvailableFittingDates: builder.query<
        Array<string>,
        { locationId: string; startDateTime?: string; endDateTime?: string; staffId?: number }
      >({
        query: ({ locationId, startDateTime, endDateTime, staffId }) =>
          AppointmentRoutes.getAvailableFittingDates(
            locationId,
            startDateTime,
            endDateTime,
            staffId,
          ),
        providesTags: ['ClubFittingAppointmentsDates'],
      }),
      logInWithEmail: builder.mutation<
        void,
        { email: string; locationId: string; bookingUUID?: string }
      >({
        query: (body) => ({
          url: AuthRoutes.loginWithEmail(),
          method: 'POST',
          body,
        }),
        invalidatesTags: ['User'],
      }),
      logInWithProvider: builder.mutation<AuthDto.LoginWithProviderResponse, { idToken: string }>({
        query: (body) => ({
          url: AuthRoutes.loginWithProvider(),
          method: 'POST',
          body,
        }),
        invalidatesTags: ['User'],
      }),
      registerPushNotificationToken: builder.mutation<void, { token: string | null }>({
        query: (body) => ({
          url: AuthRoutes.registerPushToken(),
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['User'],
      }),
      mobileLogInWithEmail: builder.mutation<void, { email: string }>({
        query: (body) => ({
          url: AuthRoutes.loginWithEmail(),
          method: 'POST',
          body,
        }),
        invalidatesTags: ['User'],
      }),
      createAccount: builder.mutation<
        void,
        { registerValues: AuthDto.RegisterDto; bookingUUID?: string }
      >({
        query: ({ registerValues, bookingUUID }) => ({
          url: AuthRoutes.createAccount(),
          method: 'POST',
          body: {
            ...registerValues,
            bookingUUID,
          },
        }),
      }),
      bookAppointment: builder.mutation<
        AppointmentTypes.BookedAppointmentResponse,
        BookAppointmentValues
      >({
        query: ({ locationId, body }) => ({
          url: AppointmentRoutes.bookAppointment(locationId),
          method: 'POST',
          body,
        }),
        invalidatesTags: [
          'SimulatorAppointments',
          'SimulatorAppointmentsDates',
          'LessonAppointments',
          'FirstLessonAppointments',
          'ClientAppointments',
        ],
      }),
      getAppointment: builder.query<AppointmentTypes.BookedAppointmentResponse, string>({
        query: (appointmentId) => AppointmentRoutes.getAppointmentById(appointmentId),
      }),
      sendWaiverLinksToGuests: builder.mutation<
        void,
        { locationId: string; body: AppointmentTypes.BookingWaiverRequest }
      >({
        query: ({ locationId, body }) => ({
          url: AppointmentRoutes.sendWaiverLinksToGuests(locationId),
          method: 'POST',
          body,
        }),
      }),
      runSyncPrices: builder.mutation<void, { siteId: string }>({
        query: ({ siteId }) => ({
          url: ScheduleRoutes.runSyncPrices(siteId),
          method: 'POST',
          body: {},
        }),
      }),
      verifyUser: builder.query<
        LoginWithVerificationTokenOutputBoundary,
        {
          token: string;
          email: string;
          redirectToUrl?: string;
          appVersion?: string;
          buildVersion?: string;
        }
      >({
        query: ({ token, email, redirectToUrl = '', appVersion, buildVersion }) => ({
          url: AuthRoutes.verifyUser(token, encodeURIComponent(email), redirectToUrl),
          headers: {
            'App-Version': appVersion,
            'Build-Version': buildVersion,
          },
        }),
      }),
      getUserData: builder.query<UserTypes.UserSummary, string>({
        query: (locationId) => AuthRoutes.getUserData(locationId),
        providesTags: ['User'],
      }),
      updateUserData: builder.mutation<void, { updateValues: AuthDto.UpdateDto }>({
        query: ({ updateValues }) => ({
          url: AuthRoutes.updateUserData(),
          method: 'PUT',
          body: updateValues,
        }),
        invalidatesTags: ['User'],
      }),
      updatePaymentDetails: builder.mutation<
        void,
        { locationId: string; body: PaymentDto.ClientDetailsDto }
      >({
        query: ({ locationId, body }) => ({
          url: AuthRoutes.updateBilling(locationId),
          method: 'POST',
          body,
        }),
        invalidatesTags: ['User'],
      }),
      updateUserProvider: builder.mutation<void, AuthDto.CreateOrUpdateUserProvider>({
        query: (values) => ({
          url: AuthRoutes.updateUserProvider(),
          method: 'POST',
          body: values,
        }),
        invalidatesTags: ['User'],
      }),
      refreshAuthToken: builder.mutation<{ accessToken: string; refreshToken: string }, string>({
        query: (refreshToken) => ({
          url: AuthRoutes.refreshToken(),
          method: 'POST',
          body: { refreshToken },
        }),
      }),
      retrieveBookingData: builder.query<AppointmentTypes.AppointmentBooking, string>({
        query: (bookingUUID) => AppointmentRoutes.retrieveBookingData(bookingUUID),
      }),
      saveBookingData: builder.mutation<string, AppointmentTypes.AppointmentBooking>({
        query: (booking) => ({
          url: AppointmentRoutes.storeBookingData(),
          method: 'POST',
          responseHandler: 'text',
          body: booking,
        }),
      }),
      deleteAccount: builder.mutation<void, void>({
        query: () => ({
          url: AuthRoutes.deleteAccount(),
          method: 'DELETE',
        }),
      }),
      checkInForAppointments: builder.mutation<
        void,
        { locationId: string; appointmentIds: number[] }
      >({
        query: ({ locationId, appointmentIds }) => ({
          url: AppointmentRoutes.checkIn(locationId),
          method: 'POST',
          body: { appointmentIds: appointmentIds },
        }),
      }),
      getLocationEventsCalendar: builder.query<Array<CalendarEventTypes.CalendarEvent>, string>({
        query: (locationId) => LocationRoutes.getLocationEventsCalendar(locationId),
      }),
      getFoodItemList: builder.query<
        StoreTypes.ProductResponse,
        { locationId: string; categoryIds?: string; limit?: number; cursor?: string }
      >({
        query: ({ locationId, categoryIds, limit, cursor }) =>
          FoodOrderRoutes.getFoodItemList(locationId, categoryIds, limit, cursor),
        providesTags: ['FoodItemList'],
      }),
      getFoodCategoryList: builder.query<
        StoreTypes.ProductCategoryResponse,
        { locationId: string; startDateTime: string }
      >({
        query: ({ locationId, startDateTime }) =>
          FoodOrderRoutes.getFoodCategoryList(locationId, startDateTime),
      }),
      savePreOrderFoodData: builder.mutation<
        void,
        { locationId: string; body: AppointmentTypes.FoodOrderRequest }
      >({
        query: ({ locationId, body }) => ({
          url: FoodOrderRoutes.storePreOrderFoodData(locationId),
          method: 'PUT',
          body,
        }),
      }),
      sendGtmEvent: builder.mutation<void, object>({
        query: (event) => ({
          url: EventRoutes.sendGtmEvent(),
          method: 'POST',
          body: event,
        }),
      }),
      getAppointmentPointsPricing: builder.mutation<
        { points: number },
        { price: number | undefined }
      >({
        query: (event) => ({
          url: AppointmentRoutes.getAppointmentPointsPricing(),
          method: 'POST',
          body: event,
        }),
      }),
      getAppointmentPointsToEarn: builder.mutation<
        { points: number },
        { appointmentType: string; price: number | undefined; endDateTime: string | undefined }
      >({
        query: (event) => ({
          url: AppointmentRoutes.getAppointmentPointsToEarn(),
          method: 'POST',
          body: event,
        }),
      }),
      getLoyaltyTransactionHistory: builder.query<LoyaltyTypes.LoyaltyCardTransaction[], void>({
        query: AppointmentRoutes.getLoyaltyTransactionHistory,
      }),
      getLoyaltyMyCards: builder.query<LoyaltyTypes.GiftCard[], void>({
        query: AppointmentRoutes.getMyCards,
        providesTags: ['GiftCards'],
      }),
      redeemGiftCard: builder.mutation<
        void,
        {
          rewardId: string;
          points: number;
        }
      >({
        query: (event) => ({
          url: AppointmentRoutes.redeemGiftCard(),
          method: 'POST',
          responseHandler: 'content-type',
          body: event,
        }),
        invalidatesTags: ['GiftCards'],
      }),
      finalizeVoucher: builder.mutation<void, { voucherId: string }>({
        query: ({ voucherId }) => ({
          url: AppointmentRoutes.voucherUsage(voucherId),
          method: 'POST',
        }),
      }),
      getRewards: builder.query<LoyaltyTypes.Reward[], string>({
        query: AppointmentRoutes.getRewards,
      }),
      getVouchers: builder.query<LoyaltyTypes.VoucherResponse[], string | undefined>({
        query: AppointmentRoutes.getVouchers,
      }),
      returnGiftCard: builder.mutation<void, { giftCardGAN: string }>({
        query: ({ giftCardGAN }) => ({
          url: AppointmentRoutes.returnGiftCard(giftCardGAN),
          method: 'POST',
        }),
      }),
      postInitialPointsBonus: builder.mutation<void, undefined>({
        query: () => ({
          url: AppointmentRoutes.postInitialPointsBonus(),
          method: 'POST',
          responseHandler: 'content-type',
          body: {
            type: 'initiation_bonus',
          },
        }),
      }),
      getCoursesRegions: builder.query<Array<CourseTypes.CourseRegion>, void>({
        query: () => CourseRoutes.getCoursesRegions(),
      }),
      getAvailableCourses: builder.query<CourseTypes.Course[], { courseRegionId: string }>({
        query: ({ courseRegionId }) => CourseRoutes.getAvailableCourses(courseRegionId),
      }),
      requestTeeTime: builder.mutation<CourseTypes.CourseRequest, CourseTypes.CourseRequestDTO>({
        query: (body) => ({
          url: CourseRoutes.requestTeeTime(),
          method: 'POST',
          responseHandler: 'content-type',
          body: body,
        }),
      }),
      submitTournamentSupportQuestion: builder.mutation<
        void,
        { message: string; tournamentName?: string; bayName?: string }
      >({
        query: (body) => ({
          url: SupportRoutes.submitTournamentSupportQuestion(),
          method: 'POST',
          responseHandler: 'json',
          body,
        }),
      }),
      getLocationGroup: builder.query<
        { locations: Array<{ id: string; name: string; siteId: number }> },
        string
      >({
        query: (locationId) => RegionalBookingRoutes.getLocationGroup(locationId),
      }),
    }),
  });

export default mboAPI;
